import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export interface ActivityDeviceSummary {
  id: string;
  device_name: string;
  device_type: string;
}

export interface Activity {
  id: number;
  project_id: number;
  activity_name: string;
  from_date: string;
  to_date: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
  devices: ActivityDeviceSummary[];
}

export interface CreateActivityRequest {
  project_id: number;
  activity_name: string;
  from_date: string;
  to_date: string;
  notes?: string;
  device_ids: string[];
}

export interface UpdateActivityRequest {
  activity_name?: string;
  from_date?: string;
  to_date?: string;
  notes?: string;
  device_ids?: string[];
}

export interface ListActivitiesParams {
  project_id?: number;
  from?: string;
  to?: string;
}

export interface ActivityProject {
  id: number;
  name: string;
  description?: string;
  layout_image_filename?: string;
  created_at: string;
  updated_at: string;
}

export interface ActivityMonitoringDevice {
  id: string;
  device_name: string;
  device_type: string;
}

export const activitiesApi = createApi({
  reducerPath: "activitiesApi",
  baseQuery: fetchBaseQuery({ baseUrl: process.env.REACT_APP_API_URL }),
  tagTypes: ["Activity", "ActivityProject", "ActivityDevice"],
  endpoints: (builder) => ({
    getProjects: builder.query<ActivityProject[], void>({
      query: () => ({ url: "projects", method: "GET" }),
      providesTags: ["ActivityProject"],
    }),

    getDevicesByProject: builder.query<ActivityMonitoringDevice[], number>({
      query: (projectId) => ({
        url: `monitoring-devices?projectId=${projectId}`,
        method: "GET",
      }),
      providesTags: (result, error, projectId) => [
        { type: "ActivityDevice", id: `project-${projectId}` },
      ],
    }),

    getActivities: builder.query<Activity[], ListActivitiesParams>({
      query: (params = {}) => {
        const sp = new URLSearchParams();
        if (params.project_id != null)
          sp.append("project_id", String(params.project_id));
        if (params.from) sp.append("from", params.from);
        if (params.to) sp.append("to", params.to);
        const qs = sp.toString();
        return { url: `activities${qs ? `?${qs}` : ""}` };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map((a) => ({ type: "Activity" as const, id: a.id })),
              { type: "Activity" as const, id: "LIST" },
            ]
          : [{ type: "Activity" as const, id: "LIST" }],
    }),

    getActivity: builder.query<Activity, number>({
      query: (id) => ({ url: `activities/${id}` }),
      providesTags: (result, error, id) => [{ type: "Activity", id }],
    }),

    createActivity: builder.mutation<Activity, CreateActivityRequest>({
      query: (body) => ({ url: "activities", method: "POST", body }),
      invalidatesTags: [{ type: "Activity", id: "LIST" }],
    }),

    updateActivity: builder.mutation<
      Activity,
      { id: number; body: UpdateActivityRequest }
    >({
      query: ({ id, body }) => ({
        url: `activities/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Activity", id },
        { type: "Activity", id: "LIST" },
      ],
    }),

    deleteActivity: builder.mutation<void, number>({
      query: (id) => ({ url: `activities/${id}`, method: "DELETE" }),
      invalidatesTags: (result, error, id) => [
        { type: "Activity", id },
        { type: "Activity", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetProjectsQuery,
  useGetDevicesByProjectQuery,
  useGetActivitiesQuery,
  useGetActivityQuery,
  useCreateActivityMutation,
  useUpdateActivityMutation,
  useDeleteActivityMutation,
} = activitiesApi;
